import "./MediaPreview.scss";
import clsx from "clsx";
import {
  selectMedia,
  deselectMedia,
  makeSelectIsMediaSelected,
  makeSelectThumbnailByFileName,
  useAppDispatch,
  useAppSelector,
  MediaType,
} from "../../../store";
import {IconTrash, IconEyeOff, IconVideoFilled} from "@tabler/icons-react";
import {memo, useMemo, useRef, useEffect} from "react";
import { useTranslation } from "react-i18next";
import {CircleLoader} from "../loader";

type MediaPreviewProps = {
  fileName: string;
  style?: React.CSSProperties;
  onHeightChange?: (index: number, height: number) => void;
  index?: number;
}

export const MediaPreview = memo(({fileName, style, onHeightChange, index}: MediaPreviewProps) => {

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onHeightChange || index === undefined) return;
    const el = itemRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      onHeightChange(index, el.offsetHeight);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange, index]);

  // Create memoized selector instances
  const selectIsSelected = useMemo(makeSelectIsMediaSelected, []);
  const selectThumbnail = useMemo(makeSelectThumbnailByFileName, []);
  
  const isSelected = useAppSelector((state) => selectIsSelected(state, fileName));
  const thumbnail = useAppSelector((state) => selectThumbnail(state, fileName));

  const onClick = () => {
    isSelected
      ? dispatch(deselectMedia(fileName))
      : dispatch(selectMedia(fileName));
  };

  const isVideo = thumbnail?.mediaType === MediaType.VIDEO;
  const thumbnailUrl = thumbnail?.thumbnailUrl;

  return (
    <div 
      ref={itemRef}
      className={clsx("media-preview", { "selected": isSelected, "loading": thumbnailUrl === undefined })}
      style={style}
      onClick={onClick}
    >
      { isSelected && <div className="delete-icon"><IconTrash /></div> }
      { isVideo && <div className="video-icon"><IconVideoFilled /></div> }
      {
        (thumbnailUrl === undefined) && (
          <div className="loader-wrap" role="status" aria-label={t("common.preparingPreview")}>
            <CircleLoader />
          </div>
        )
      }
      {
        (thumbnailUrl === null) && (
          <>
            <div className="icon">
              <IconEyeOff />
            </div>
            <span>Náhled se nepodařilo připravit, soubor je přesto možné odeslat.</span>
          </>
        )
      }

      { thumbnailUrl && <img src={thumbnailUrl} alt={fileName} />}
    </div>
  );
});

MediaPreview.displayName = 'MediaPreview';